import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getRenewalCalendar } from "api/onboardingApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /compliance/renewals — admin renewal calendar.
 *
 * Buckets:
 *   • Overdue
 *   • This week (next 7 days)
 *   • Next 30 days
 *   • Next 90 days
 *
 * Rows: one per expiring training / policy with driver, item, exact date,
 * days-until, status. Click → driver compliance page.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const KIND_COLORS = {
  TRAINING: { bg: "#fef3c7", fg: "#92400e", label: "Training" },
  POLICY: { bg: "#e0e7ff", fg: "#3730a3", label: "Policy" },
};

function bucketFor(days) {
  if (days < 0) return "overdue";
  if (days <= 7) return "thisWeek";
  if (days <= 30) return "next30";
  return "next90";
}

const BUCKETS = [
  { key: "overdue", label: "Overdue", color: "#dc2626" },
  { key: "thisWeek", label: "This week", color: "#f97316" },
  { key: "next30", label: "Next 30 days", color: "#3b82f6" },
  { key: "next90", label: "Next 90 days", color: "#16a34a" },
];

export const RenewalCalendarPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [bucketFilter, setBucketFilter] = useState("all");
  const [horizon, setHorizon] = useState(90);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = Date.now();
      const fromMs = now - 30 * DAY_MS;
      const toMs = now + horizon * DAY_MS;
      const res = await getRenewalCalendar({ fromMs, toMs });
      setEntries(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load renewals");
    } finally {
      setLoading(false);
    }
  }, [horizon]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (kindFilter !== "all" && e.kind !== kindFilter) return false;
      if (bucketFilter !== "all" && bucketFor(e.daysUntilExpiry ?? 0) !== bucketFilter) return false;
      if (!q) return true;
      const hay = `${e.driverFullName} ${e.title} ${e.code} ${e.driverEmail || ""} ${e.driverPhone || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [entries, search, kindFilter, bucketFilter]);

  const grouped = useMemo(() => {
    const g = { overdue: [], thisWeek: [], next30: [], next90: [] };
    filtered.forEach((e) => {
      const b = bucketFor(e.daysUntilExpiry ?? 0);
      g[b].push(e);
    });
    return g;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { overdue: 0, thisWeek: 0, next30: 0, next90: 0, total: entries.length };
    entries.forEach((e) => {
      const b = bucketFor(e.daysUntilExpiry ?? 0);
      c[b] = (c[b] || 0) + 1;
    });
    return c;
  }, [entries]);

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Renewal Calendar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Trainings + policy acknowledgments expiring in the window. Triage overdue first, then this-week, then plan ahead.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            select
            SelectProps={{ native: true }}
            size="small"
            value={horizon}
            onChange={(e) => setHorizon(Number(e.target.value))}
            label="Horizon"
            sx={{ width: 130 }}
            InputLabelProps={{ shrink: true }}
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </TextField>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      {/* Bucket KPIs */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {BUCKETS.map((b) => (
          <Card
            key={b.key}
            variant="outlined"
            sx={{
              flex: 1,
              minWidth: 140,
              borderRadius: 2,
              cursor: "pointer",
              borderColor: bucketFilter === b.key ? b.color : "#e2e8f0",
              borderWidth: bucketFilter === b.key ? 2 : 1,
            }}
            onClick={() => setBucketFilter(bucketFilter === b.key ? "all" : b.key)}
          >
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{b.label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: b.color }}>{counts[b.key] || 0}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search driver / item / company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        {["all", "TRAINING", "POLICY"].map((k) => (
          <Chip
            key={k}
            size="small"
            label={k === "all" ? "All" : KIND_COLORS[k]?.label}
            onClick={() => setKindFilter(k)}
            sx={{
              backgroundColor: kindFilter === k ? "#1F4E78" : "#f1f5f9",
              color: kindFilter === k ? "#ffffff" : "#475569",
              fontWeight: kindFilter === k ? 700 : 500,
              cursor: "pointer",
            }}
          />
        ))}
      </Box>

      {loading && entries.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        BUCKETS.filter((b) => grouped[b.key].length > 0).map((b) => (
          <Card key={b.key} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: b.color }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{b.label}</Typography>
                <Chip size="small" label={grouped[b.key].length} sx={{ ml: 1 }} />
              </Box>
              <Stack divider={<Divider />} spacing={0}>
                {grouped[b.key].map((e) => {
                  const kc = KIND_COLORS[e.kind] || KIND_COLORS.TRAINING;
                  const daysLabel =
                    e.daysUntilExpiry < 0
                      ? `${Math.abs(e.daysUntilExpiry)}d overdue`
                      : e.daysUntilExpiry === 0
                      ? "today"
                      : `in ${e.daysUntilExpiry}d`;
                  return (
                    <Box
                      key={`${e.kind}-${e.itemId}`}
                      sx={{ py: 1.25, display: "flex", gap: 2, alignItems: "center" }}
                    >
                      <Box sx={{ flex: 2, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {e.driverFullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {e.driverEmail || e.driverPhone || "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 3, minWidth: 0 }}>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <Chip
                            size="small"
                            label={kc.label}
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: kc.bg, color: kc.fg }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {e.title}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {e.code} · {e.status}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: "right" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: e.daysUntilExpiry < 0 ? "#dc2626" : e.daysUntilExpiry <= 7 ? "#f97316" : "#475569",
                          }}
                        >
                          {daysLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(e.expiresAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() => navigate(`/compliance/drivers/${e.driverDetailsId}`)}
                      >
                        Open
                      </Button>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        ))
      )}

      {!loading && filtered.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              {entries.length === 0
                ? "No renewals due in the selected window. 🎉"
                : "No renewals match the current filter."}
            </Typography>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
};

export default RenewalCalendarPage;
